import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ReactMapGL, { Layer, Marker, NavigationControl, Popup, Source } from 'react-map-gl'
import { useSelector } from 'react-redux'
import {
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  Dialog,
  InputAdornment,
  Snackbar,
  Typography,
} from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import * as turf from '@turf/turf'
import debounce from 'lodash/debounce'
import 'mapbox-gl/dist/mapbox-gl.css'

import { apiZodios } from '~api/apiZodios'
import { Banner, Button, Icon, IconButton, SearchBase, TextField } from '~components/internal'
import { MAPBOX_TOKEN } from '~config'
import { type RootState } from '~redux/rootReducer'
import { type Address0, type GISLayerSourceId, type GISPolygonId, type IdentityId } from '~types'
import { isValidState, isValidZip } from '~utils/addressUtils'
import {
  type MapboxPlaceSuggestion,
  createMapboxSessionToken,
  retrieveMapboxPlace,
  suggestMapboxPlaces,
} from '~utils/mapboxSearchUtils'

const DEFAULT_COORDINATES = { lat: 40.758, lon: -73.9855 }

interface IMapboxContext {
  id: string
  text: string
  short_code?: string
}

interface IMapboxFeature {
  place_name: string
  text: string
  center: [number, number]
  context?: IMapboxContext[]
}

interface IMapboxResponse {
  features: IMapboxFeature[]
}

interface IMarkerDragEvent {
  lngLat: {
    lng: number
    lat: number
  }
}

interface IMapMoveEvent {
  viewState: {
    longitude: number
    latitude: number
    zoom: number
  }
  target: {
    getCenter: () => {
      lng: number
      lat: number
    }
  }
}

const parseMapboxAddress = (
  feature: IMapboxFeature,
): {
  street: string
  unit: string
  city: string
  state: string
  zip: string
} => {
  const components = {
    street: '',
    unit: '',
    city: '',
    state: '',
    zip: '',
  }

  if (!feature.context) return components

  // Extract address components from Mapbox response
  feature.context.forEach((ctx: IMapboxContext) => {
    if (ctx.id.startsWith('postcode')) components.zip = ctx.text
    if (ctx.id.startsWith('place')) components.city = ctx.text
    if (ctx.id.startsWith('region')) {
      components.state = ctx.short_code?.split('-')[1] || ''
    }
  })

  // Get street address from place name
  components.street = feature.place_name.split(',')[0]

  return components
}

const OverlayLayers: React.FC<{
  overlayGeometry: GeoJSON.FeatureCollection
  overlayPopup: {
    longitude: number
    latitude: number
    name: string
    description: string | null
  } | null
  onClosePopup: () => void
}> = ({ overlayGeometry, overlayPopup, onClosePopup }) => (
  <>
    <Source id='overlay-layer' type='geojson' data={overlayGeometry}>
      {/* Polygon fills */}
      <Layer
        id='overlay-polygon-fill'
        type='fill'
        filter={['==', ['get', 'layerType'], 'polygon']}
        paint={{
          'fill-color': '#1976D2',
          'fill-opacity': 0.25,
        }}
      />
      {/* Polygon outlines */}
      <Layer
        id='overlay-polygon-outline'
        type='line'
        filter={['==', ['get', 'layerType'], 'polygon']}
        paint={{
          'line-color': '#1565C0',
          'line-width': 2,
        }}
      />
      {/* Lines */}
      <Layer
        id='overlay-line'
        type='line'
        filter={['==', ['get', 'layerType'], 'line']}
        paint={{
          'line-color': '#1565C0',
          'line-width': 2,
        }}
      />
      {/* Points */}
      <Layer
        id='overlay-point'
        type='circle'
        filter={['==', ['get', 'layerType'], 'point']}
        paint={{
          'circle-radius': 6,
          'circle-color': '#1976D2',
          'circle-stroke-width': 2,
          'circle-stroke-color': '#FFFFFF',
        }}
      />
    </Source>
    {overlayPopup && (
      <Popup
        longitude={overlayPopup.longitude}
        latitude={overlayPopup.latitude}
        anchor='bottom'
        onClose={onClosePopup}
        closeOnClick={true}
      >
        <Box sx={{ p: 0.5, maxWidth: 200 }}>
          <Typography variant='body2' sx={{ fontWeight: 600 }}>
            {overlayPopup.name}
          </Typography>
          {overlayPopup.description && (
            <Typography
              variant='caption'
              sx={{
                color: 'text.secondary',
              }}
            >
              {overlayPopup.description}
            </Typography>
          )}
        </Box>
      </Popup>
    )}
  </>
)

interface LocationEntryModalProps {
  onClose: () => void
  onSave: (address: Address0) => void
  title?: string
  address?: Address0
  organizationId?: string
  requestAddress?: boolean
  requestCoordinates?: boolean
  validArea?: GISPolygonId
  overlay?: GISLayerSourceId
}

const LocationEntryModal: React.FC<LocationEntryModalProps> = ({
  onClose,
  onSave,
  title = 'Enter Location',
  address,
  organizationId,
  requestAddress,
  requestCoordinates,
  validArea = undefined,
  overlay = undefined,
}) => {
  // Refs for focus management
  const searchInputRef = useRef<HTMLInputElement>(null)
  const backButtonRef = useRef<HTMLButtonElement>(null)
  const confirmButtonRef = useRef<HTMLButtonElement>(null)
  const streetInputRef = useRef<HTMLInputElement>(null)
  const mapContainerRef = useRef<HTMLDivElement>(null)
  // View states
  const user = useSelector((state: RootState) => state?.auth.user)
  const [view, setView] = useState<'search' | 'manual' | 'map' | 'confirm'>(
    address ? 'confirm' : requestCoordinates && !requestAddress ? 'map' : 'search',
  )
  const [searchValue, setSearchValue] = useState(
    address ? `${address.street}, ${address.city}, ${address.state} ${address.zip}` : '',
  )
  const [isSearching, setIsSearching] = useState(false)
  const [suggestions, setSuggestions] = useState<MapboxPlaceSuggestion[]>([])
  const sessionTokenRef = useRef(createMapboxSessionToken())
  const searchBiasRef = useRef(DEFAULT_COORDINATES)
  const [error, setError] = useState<string | null>(null)
  const [announcement, setAnnouncement] = useState('')
  const [showAnnouncement, setShowAnnouncement] = useState(false)
  const [isLocationValid, setIsLocationValid] = useState<boolean>(true)
  const [validAreaGeometry, setValidAreaGeometry] = useState<string | null>(null)
  const [overlayGeometry, setOverlayGeometry] = useState<GeoJSON.FeatureCollection | null>(null)
  const [overlayPopup, setOverlayPopup] = useState<{
    longitude: number
    latitude: number
    name: string
    description: string | null
  } | null>(null)
  const [validationErrors, setValidationErrors] = useState<{
    state: string | null
    zip: string | null
  }>({ state: null, zip: null })

  // Location data
  const [selectedLocation, setSelectedLocation] = useState({
    address: {
      street: address?.street ?? '',
      unit: '',
      city: address?.city ?? '',
      state: address?.state ?? '',
      zip: address?.zip ?? '',
    },
    coordinates: address?.location ?? DEFAULT_COORDINATES,
  })
  const selectedCoordinatesRef = useRef(selectedLocation.coordinates)
  selectedCoordinatesRef.current = selectedLocation.coordinates

  const updateLocationFromGeocode = async (lat: number, lng: number): Promise<void> => {
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${MAPBOX_TOKEN}`,
      )
      if (!response.ok) {
        console.error('Search failed')
        setError('Search failed')
        return
      }
      const data: IMapboxResponse = await response.json()
      if (data.features?.[0]) {
        const addressComponents = parseMapboxAddress(data.features[0])
        setSelectedLocation({
          address: addressComponents,
          coordinates: { lat, lon: lng },
        })
      }
    } catch (error) {
      console.error('Reverse geocoding error:', error)
      setError('Failed to get address from longitude and latitude.')
    }
  }

  const debouncedUpdateLocationFromGeocode = useMemo(
    () => debounce((lat: number, lng: number) => updateLocationFromGeocode(lat, lng), 300),
    [],
  )

  useEffect(() => {
    return (): void => {
      debouncedUpdateLocationFromGeocode.cancel()
    }
  }, [debouncedUpdateLocationFromGeocode])

  // Map view states
  const [showSatellite, setShowSatellite] = useState(false)
  const [mapZoom, setMapZoom] = useState(14)

  const [coordinateInputs, setCoordinateInputs] = useState({
    lat: selectedLocation.coordinates.lat.toString(),
    lon: selectedLocation.coordinates.lon.toString(),
  })

  const resetToSearchView = (): void => {
    setView('search')
    setSearchValue(
      address ? `${address.street}, ${address.city}, ${address.state} ${address.zip}` : '',
    )
    setSelectedLocation({
      address: {
        street: address?.street ?? '',
        unit: '',
        city: address?.city ?? '',
        state: address?.state ?? '',
        zip: address?.zip ?? '',
      },
      coordinates: address?.location ?? searchBiasRef.current,
    })
    announceToScreenReader('Returned to search view')
  }

  // Add viewState for ReactMapGL
  const [viewState, setViewState] = useState({
    longitude: selectedLocation.coordinates.lon,
    latitude: selectedLocation.coordinates.lat,
    zoom: 14,
  })

  // Announce changes to screen readers
  const announceToScreenReader = (message: string): void => {
    setAnnouncement(message)
    setShowAnnouncement(true)
    setTimeout(() => setShowAnnouncement(false), 3000)
  }

  // Track the currently focused option for accessibility
  const [focusedOption, setFocusedOption] = useState<string | null>(null)

  // Add these hooks at the top of the LocationEntryModal component
  const [isTouchDevice, setIsTouchDevice] = useState(false)

  useEffect(() => {
    // Check for touch capability
    const touchQuery = window.matchMedia('(hover: none) and (pointer: coarse)')
    setIsTouchDevice(touchQuery.matches)

    // Optional: Listen for changes (e.g., if device switches input modes)
    const updateTouchStatus = (e: MediaQueryListEvent): void => setIsTouchDevice(e.matches)
    touchQuery.addEventListener('change', updateTouchStatus)
    return (): void => touchQuery.removeEventListener('change', updateTouchStatus)
  }, [])

  // Focus management when view changes
  useEffect(() => {
    // Set focus to the appropriate element when view changes
    setTimeout(() => {
      switch (view) {
        case 'search':
          searchInputRef.current?.focus()
          announceToScreenReader('Address search view loaded')
          break
        case 'manual':
          streetInputRef.current?.focus()
          announceToScreenReader('Manual address entry view loaded')
          break
        case 'map':
          mapContainerRef.current?.focus()
          announceToScreenReader('Map selection view loaded')
          break
        case 'confirm':
          streetInputRef.current?.focus()
          announceToScreenReader('Confirm address view loaded')
          break
      }
    }, 100)
  }, [view])

  useEffect(() => {
    if (validAreaGeometry && selectedLocation.coordinates) {
      const valid = isPointInPolygon(
        [selectedLocation.coordinates.lon, selectedLocation.coordinates.lat],
        JSON.parse(validAreaGeometry),
      )
      setIsLocationValid(valid)
      if (!valid) {
        announceToScreenReader('Warning: Selected location is outside the valid service area')
      }
    }
  }, [selectedLocation.coordinates, validAreaGeometry])

  // Keep a ref to the latest coordinates so the debounced callback always reads
  // the current value without needing to be recreated.
  const coordinatesRef = useRef(selectedLocation.coordinates)
  coordinatesRef.current = selectedLocation.coordinates

  // Update search handling to use proximity
  const handleSearchRef = useRef(
    debounce(async (value: string): Promise<void> => {
      if (value.length < 3) {
        setSuggestions([])
        return
      }

      setIsSearching(true)
      setError(null)

      try {
        setSuggestions(
          await suggestMapboxPlaces(value, {
            sessionToken: sessionTokenRef.current,
            proximity: coordinatesRef.current,
          }),
        )
      } catch (error) {
        console.error('Search error:', error)
        setError('Failed to search for a location. Please try again.')
        setSuggestions([])
      } finally {
        setIsSearching(false)
      }
    }, 300),
  )
  const handleSearch = handleSearchRef.current

  // Cancel pending debounced search on unmount
  useEffect(() => {
    return (): void => {
      handleSearchRef.current.cancel()
    }
  }, [])

  // Add useEffect to handle initial location
  useEffect(() => {
    const fetchLocation = async (): Promise<void> => {
      if (!user) return
      try {
        let location
        if (organizationId) {
          const response = await apiZodios.contacts_getOrganizationLocation({
            organization_identity_id: organizationId,
            user_id: user.id,
          })
          location = response.point
        }
        if (!location) {
          const response = await apiZodios.contacts_getMyLocation({ user_id: user.id })
          location = response?.point
        }

        if (location) {
          searchBiasRef.current = location
          if (selectedCoordinatesRef.current === DEFAULT_COORDINATES) {
            setSelectedLocation((prev) => ({ ...prev, coordinates: location }))
            setViewState({ longitude: location.lon, latitude: location.lat, zoom: 14 })
          }
        }
      } catch (error) {
        console.error('Error fetching location:', error)
      }
    }

    if (!address) {
      fetchLocation()
    }
  }, [organizationId, address])

  const handleSuggestionSelect = async (selected: MapboxPlaceSuggestion | null): Promise<void> => {
    if (!selected) return

    try {
      const place = await retrieveMapboxPlace(selected.mapbox_id, {
        sessionToken: sessionTokenRef.current,
      })
      sessionTokenRef.current = createMapboxSessionToken()

      if (!place) {
        setError('Failed to process location. Please try again.')
        return
      }

      const [lon, lat] = place.center

      // Update both selectedLocation and viewState
      setSelectedLocation({
        address: place.address,
        coordinates: { lat, lon },
      })
      setViewState({
        longitude: lon,
        latitude: lat,
        zoom: 14,
      })
      setCoordinateInputs({
        lat: lat.toFixed(6),
        lon: lon.toFixed(6),
      })

      // Switch to confirm view
      setView('confirm')
    } catch (error) {
      console.error('Geocoding error:', error)
      setError('Failed to process location. Please try again.')
    }
  }

  const loadGISPolygon = async (): Promise<void> => {
    if (!validArea || !user) return
    const { polygons: polygons } = await apiZodios.ai_getGISPolygons({
      user_id: user.id,
      gis_polygon_ids: [validArea],
    })
    setValidAreaGeometry(polygons[0]?.geometry || null)
  }

  const loadGISOverlay = async (): Promise<void> => {
    if (!overlay || !user || !organizationId) return
    try {
      const {
        polygons = [],
        lines = [],
        points = [],
      } = await apiZodios.ai_getGISLayerPolygons({
        user_id: user.id,
        organization_identity_id: organizationId as IdentityId,
        gis_layer_id: overlay,
      })

      const features: GeoJSON.Feature[] = []

      polygons.forEach((p: { geometry?: string }) => {
        if (p.geometry) {
          features.push({
            type: 'Feature',
            properties: { layerType: 'polygon' },
            geometry: JSON.parse(p.geometry),
          })
        }
      })

      lines.forEach((l: { geometry?: string }) => {
        if (l.geometry) {
          features.push({
            type: 'Feature',
            properties: { layerType: 'line' },
            geometry: JSON.parse(l.geometry),
          })
        }
      })

      points.forEach((pt) => {
        if (pt.geometry) {
          features.push({
            type: 'Feature',
            properties: { layerType: 'point', name: pt.name, description: pt.description },
            geometry: JSON.parse(pt.geometry),
          })
        }
      })

      if (features.length > 0) {
        setOverlayGeometry({
          type: 'FeatureCollection',
          features,
        })
      }
    } catch (error) {
      console.error('Error loading GIS overlay:', error)
    }
  }

  useEffect(() => {
    if (validArea && !validAreaGeometry) {
      void loadGISPolygon()
    }
  }, [validArea, validAreaGeometry])

  useEffect(() => {
    if (overlay && !overlayGeometry) {
      void loadGISOverlay()
    }
  }, [overlay, overlayGeometry])

  const isPointInPolygon = (point: [number, number], geometry: GeoJSON.Geometry): boolean => {
    if (!geometry) return true

    try {
      const pt = turf.point(point)

      let geoJsonFeature: GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon>

      if (geometry.type === 'Polygon' || geometry.type === 'MultiPolygon') {
        geoJsonFeature = {
          type: 'Feature' as const,
          properties: {},
          geometry: geometry as GeoJSON.Polygon | GeoJSON.MultiPolygon,
        }
      } else {
        console.warn('Unknown geometry type:', geometry.type)
        return true
      }

      return turf.booleanPointInPolygon(pt, geoJsonFeature)
    } catch (error) {
      console.error('Error checking point in polygon:', error)
      return true
    }
  }

  const handleSave = async (): Promise<void> => {
    if (!user) return
    let street = selectedLocation.address.street
    if (selectedLocation.address.unit) {
      street += ' ' + selectedLocation.address.unit
    }

    const { lat, lon } = selectedLocation.coordinates
    const roundedLocation = {
      lat: Number(lat.toFixed(6)),
      lon: Number(lon.toFixed(6)),
    }

    try {
      const response = await apiZodios.contacts_createAddress({
        user_id: user.id,
        street: street,
        city: selectedLocation.address.city,
        state: selectedLocation.address.state,
        zip: selectedLocation.address.zip,
        location: roundedLocation,
      })

      const address = response.address
      onSave(address)
    } catch (error) {
      console.error('Failed to save address:', error)
      setError('Failed to save address.')
    }
  }

  const handleOverlayPointClick = useCallback((e: mapboxgl.MapLayerMouseEvent) => {
    const feature = e.features?.[0]
    if (feature && feature.geometry.type === 'Point') {
      const [longitude, latitude] = (feature.geometry as GeoJSON.Point).coordinates
      setOverlayPopup({
        longitude,
        latitude,
        name: feature.properties?.name ?? '',
        description: feature.properties?.description ?? null,
      })
    }
  }, [])

  // Update handleMapMove to use isTouchDevice instead of window.innerWidth
  const handleMapMove = useCallback(
    async (evt: IMapMoveEvent) => {
      setViewState(evt.viewState)

      // Only update location on touch devices
      if (isTouchDevice) {
        const center = evt.target.getCenter()

        // Update marker position and coordinate inputs with 6 decimal precision
        setSelectedLocation((prev) => ({
          ...prev,
          coordinates: { lat: center.lat, lon: center.lng },
        }))
        setCoordinateInputs({
          lat: center.lat.toFixed(6),
          lon: center.lng.toFixed(6),
        })

        debouncedUpdateLocationFromGeocode(center.lat, center.lng)
      }
    },
    [isTouchDevice, debouncedUpdateLocationFromGeocode],
  )

  // Update handleMarkerDragEnd to also update coordinate inputs
  const handleMarkerDragEnd = (event: IMarkerDragEvent): void => {
    const { lng, lat } = event.lngLat
    setViewState({
      ...viewState,
      longitude: lng,
      latitude: lat,
    })
    updateLocationFromGeocode(lat, lng)
    setCoordinateInputs({
      lat: lat.toFixed(6),
      lon: lng.toFixed(6),
    })
  }

  const renderInvalidAreaMessage = (): React.ReactNode => (
    <Typography
      variant='body2'
      color='error'
      sx={{ gap: 1, mb: 2, display: 'flex', alignItems: 'center' }}
    >
      <Box>
        Select a location within the
        <Button
          variant='link'
          width='fit'
          onClick={() => {
            if (validAreaGeometry) {
              try {
                const geometry = JSON.parse(validAreaGeometry)
                const bbox = turf.bbox(geometry)
                const [minLng, minLat, maxLng, maxLat] = bbox

                const centerLng = (minLng + maxLng) / 2
                const centerLat = (minLat + maxLat) / 2

                const lngDiff = maxLng - minLng
                const latDiff = maxLat - minLat
                const maxDiff = Math.max(lngDiff, latDiff)

                let zoom = 12
                if (maxDiff < 0.01) zoom = 16
                else if (maxDiff < 0.05) zoom = 14
                else if (maxDiff < 0.1) zoom = 13
                else if (maxDiff < 0.5) zoom = 11
                else if (maxDiff < 1) zoom = 10
                else zoom = 9

                setViewState({
                  longitude: centerLng,
                  latitude: centerLat,
                  zoom: zoom,
                })
                setMapZoom(zoom)
                announceToScreenReader('Map centered on valid service area')
              } catch (error) {
                console.error('Error centering map on valid area:', error)
              }
            }
          }}
          aria-label='Zoom to valid boundary area'
        >
          valid boundary
        </Button>
      </Box>
    </Typography>
  )

  const renderSearchView = (): React.ReactNode => (
    <Box role='region'>
      <Typography variant='body1' sx={{ mb: 1, fontWeight: 500 }} id='address-search-label'>
        Address
      </Typography>
      <div className='address-search-container'>
        <Autocomplete
          freeSolo
          options={suggestions.map((s) => s.place_name)}
          loading={isSearching}
          value={searchValue}
          onChange={(_, newValue) => {
            const match = suggestions.find((s) => s.place_name === newValue)
            if (!match) return
            handleSuggestionSelect(match)
            announceToScreenReader(`Selected address: ${newValue || 'None'}`)
          }}
          onInputChange={(_, value, reason) => {
            setSearchValue(value)
            if (reason === 'input') {
              handleSearch(value)
              if (value.length < 3) {
                announceToScreenReader('Please enter at least 3 characters to search')
              } else {
                announceToScreenReader('Searching for address...')
              }
            }
          }}
          blurOnSelect={true}
          filterOptions={(x) => x}
          clearOnBlur={false}
          clearOnEscape={true}
          clearText='Clear address search'
          closeText='Close suggestions'
          openText='Open address suggestions'
          loadingText='Loading suggestions...'
          noOptionsText='No suggestions found'
          onKeyDown={(event) => {
            // Get the currently highlighted option
            const highlightedOption = document.querySelector(
              '.MuiAutocomplete-option[aria-selected="true"]',
            )

            if (event.key === 'ArrowDown') {
              const nextOption = highlightedOption?.nextElementSibling as HTMLElement
              if (nextOption) {
                const optionText = nextOption.textContent
                setFocusedOption(optionText)
                announceToScreenReader(`${optionText}`)
              }
            } else if (event.key === 'ArrowUp') {
              const prevOption = highlightedOption?.previousElementSibling as HTMLElement
              if (prevOption) {
                const optionText = prevOption.textContent
                setFocusedOption(optionText)
                announceToScreenReader(`${optionText}`)
              }
            } else if (event.key === 'Enter') {
              if (highlightedOption) {
                announceToScreenReader(`Selected ${highlightedOption.textContent}`)
              } else {
                announceToScreenReader('Selection confirmed')
              }
            } else if (event.key === 'Escape') {
              announceToScreenReader('Suggestions closed')
              setFocusedOption(null)
            }
          }}
          onHighlightChange={(event, option, reason) => {
            if (option && reason === 'keyboard') {
              setFocusedOption(option)
              announceToScreenReader(`${option}`)
            }
          }}
          renderOption={(props, option, { index }) => (
            <li
              {...props}
              id={`address-option-${index}`}
              role='option'
              aria-selected={props['aria-selected']}
              aria-label={option}
              aria-posinset={index + 1}
              aria-setsize={suggestions.length}
              tabIndex={-1}
              onFocus={() => {
                setFocusedOption(option)
                announceToScreenReader(option)
              }}
              onMouseEnter={() => {
                setFocusedOption(option)
                announceToScreenReader(option)
              }}
            >
              {option}
            </li>
          )}
          renderInput={(params) => {
            const modifiedParams = {
              ...params,
              inputProps: {
                ...(params.slotProps?.htmlInput ?? {}),
                role: 'combobox',
                'aria-autocomplete': 'list' as 'list',
                'aria-controls': 'address-suggestions-listbox',
                'aria-expanded': Boolean(suggestions.length > 0),
                'aria-owns': 'address-suggestions-listbox',
                'aria-label': 'Search for an address',
                'aria-labelledby': 'address-search-label',
                'aria-describedby': 'address-search-desc',
                'aria-activedescendant': focusedOption
                  ? `address-option-${suggestions.findIndex((s) => s.place_name === focusedOption)}`
                  : undefined,
              },
            }
            return (
              <SearchBase
                ref={modifiedParams.slotProps?.input?.ref}
                inputProps={modifiedParams.inputProps}
                inputRef={searchInputRef}
                placeholder='23 Dundee Road or Town Hall'
                aria-label='Search for an address. When suggestions appear, use arrow keys to navigate and Enter to select'
                aria-describedby='address-search-label address-search-desc'
                fullWidth
                startAdornment={
                  <InputAdornment position='start'>
                    <Icon name='Search' aria-hidden='true' />
                  </InputAdornment>
                }
                endAdornment={
                  <InputAdornment position='end'>
                    {isSearching ? (
                      <CircularProgress
                        color='inherit'
                        size={20}
                        aria-label='Loading suggestions'
                      />
                    ) : (
                      searchValue && (
                        <IconButton
                          onClick={() => {
                            setSearchValue('')
                            announceToScreenReader('Address search cleared')
                            if (searchInputRef.current) {
                              searchInputRef.current.focus()
                            }
                          }}
                          aria-label='Clear address search'
                          size='small'
                        >
                          <Icon name='Close' size={18} />
                        </IconButton>
                      )
                    )}
                  </InputAdornment>
                }
              />
            )
          }}
          slotProps={{
            listbox: {
              id: 'address-suggestions-listbox',
              'aria-label': 'Address suggestions',
              role: 'listbox',
            },

            paper: {
              'aria-label': 'Address suggestions',
              sx: {
                '& .MuiAutocomplete-listbox': {
                  '& .MuiAutocomplete-option': {
                    '&:focus-visible': {
                      outline: '2px solid',
                      outlineColor: 'primary.main',
                    },
                  },
                },
              },
            },
          }}
        />
      </div>
      {/* Hidden element for screen reader descriptions */}
      <div id='address-search-desc' style={{ ...visuallyHidden }}>
        Type at least 3 characters to search for addresses. Use arrow keys to navigate suggestions
        and Enter to select an address.
      </div>
      <Box sx={{ mt: 2 }}>
        <Typography
          variant='body2'
          sx={{
            color: 'text.secondary',
            mb: 1,
          }}
        >
          Not seeing results?
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant='link'
            width='fit'
            onClick={() => {
              setView('manual')
              // Set location so initial manual location entry form values match viewState
              updateLocationFromGeocode(viewState.latitude, viewState.longitude)
            }}
            aria-label='Enter address manually'
          >
            Enter Manually
          </Button>
          <Button
            variant='link'
            width='fit'
            onClick={() => setView('map')}
            aria-label='Select location on map'
          >
            Select on Map
          </Button>
        </Box>
      </Box>
      {/* Screen reader announcements */}
      <div aria-live='assertive' role='status' style={{ ...visuallyHidden }}>
        {isSearching ? 'Searching for addresses...' : ''}
        {suggestions.length > 0 && !focusedOption
          ? `Found ${suggestions.length} suggestions. Use arrow keys to navigate and Enter to select`
          : ''}
        {showAnnouncement && announcement}
      </div>
      {/* Additional announcement area specifically for focused options */}
      <div aria-live='assertive' role='status' style={{ ...visuallyHidden }}>
        {focusedOption}
      </div>
    </Box>
  )

  const renderManualView = (): React.ReactNode => renderConfirmView()

  const renderMapView = (): React.ReactNode => (
    <Box role='region' aria-label='Map selection'>
      <Button
        variant='outlined'
        size='medium'
        startIcon={<Icon name='MyLocation' />}
        onClick={() => {
          if (navigator.geolocation) {
            announceToScreenReader('Getting your current location...')
            navigator.geolocation.getCurrentPosition(
              (position) => {
                const { latitude, longitude } = position.coords
                setViewState({
                  longitude,
                  latitude,
                  zoom: 14,
                })
                setSelectedLocation((prev) => ({
                  ...prev,
                  coordinates: { lat: latitude, lon: longitude },
                }))
                setCoordinateInputs({
                  lat: latitude.toFixed(6),
                  lon: longitude.toFixed(6),
                })
                announceToScreenReader('Map updated to your current location')
              },
              (error) => {
                console.error('Error getting location:', error)
                setError('Unable to get your current location. Please try another method.')
                announceToScreenReader(
                  'Unable to get your current location. Please try another method.',
                )
              },
            )
          } else {
            setError('Geolocation is not supported by your browser.')
            announceToScreenReader('Geolocation is not supported by your browser.')
          }
        }}
        sx={{ mb: 2 }}
      >
        Use My Current Location
      </Button>

      {/* Add coordinate input fields */}
      {requestCoordinates && (
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            label='Latitude'
            value={coordinateInputs.lat}
            onChange={(e) => {
              const value = e.target.value
              if (value === '' || value === '-' || value.match(/^-?\d*\.?\d*$/)) {
                setCoordinateInputs((prev) => ({ ...prev, lat: value }))
                const lat = parseFloat(value)
                if (!isNaN(lat) && lat >= -90 && lat <= 90) {
                  setSelectedLocation((prev) => ({
                    ...prev,
                    coordinates: { ...prev.coordinates, lat },
                  }))
                  setViewState((prev) => ({
                    ...prev,
                    latitude: lat,
                  }))
                  announceToScreenReader(`Latitude set to ${lat}`)
                }
              }
            }}
            inputProps={{
              'aria-label': 'Latitude',
              inputMode: 'decimal',
            }}
          />
          <TextField
            label='Longitude'
            value={coordinateInputs.lon}
            onChange={(e) => {
              const value = e.target.value
              if (value === '' || value === '-' || value.match(/^-?\d*\.?\d*$/)) {
                setCoordinateInputs((prev) => ({ ...prev, lon: value }))
                const lon = parseFloat(value)
                if (!isNaN(lon) && lon >= -180 && lon <= 180) {
                  setSelectedLocation((prev) => ({
                    ...prev,
                    coordinates: { ...prev.coordinates, lon },
                  }))
                  setViewState((prev) => ({
                    ...prev,
                    longitude: lon,
                  }))
                  announceToScreenReader(`Longitude set to ${lon}`)
                }
              }
            }}
            inputProps={{
              'aria-label': 'Longitude',
              inputMode: 'decimal',
            }}
          />
        </Box>
      )}

      <Box
        sx={{ position: 'relative', height: 400 }}
        ref={mapContainerRef}
        tabIndex={0}
        aria-label={
          isTouchDevice
            ? 'Interactive map. Drag the map to position the marker.'
            : 'Interactive map. Drag the marker to set your location.'
        }
        role='application'
        onKeyDown={(e) => {
          const step = e.shiftKey ? 0.01 : 0.001
          let newLat = selectedLocation.coordinates.lat
          let newLon = selectedLocation.coordinates.lon

          switch (e.key) {
            case 'ArrowUp':
              newLat += step
              e.preventDefault()
              break
            case 'ArrowDown':
              newLat -= step
              e.preventDefault()
              break
            case 'ArrowLeft':
              newLon -= step
              e.preventDefault()
              break
            case 'ArrowRight':
              newLon += step
              e.preventDefault()
              break
          }

          if (
            newLat !== selectedLocation.coordinates.lat ||
            newLon !== selectedLocation.coordinates.lon
          ) {
            setViewState({
              longitude: newLon,
              latitude: newLat,
              zoom: viewState.zoom,
            })
            setSelectedLocation((prev) => ({
              ...prev,
              coordinates: { lat: newLat, lon: newLon },
            }))
            announceToScreenReader(
              `Marker moved to latitude ${newLat.toFixed(4)}, longitude ${newLon.toFixed(4)}`,
            )
          }
        }}
      >
        <Typography
          variant='body2'
          sx={{
            position: 'absolute',
            top: 10,
            left: 10,
            bgcolor: 'rgba(255, 255, 255, 0.8)',
            p: 1,
            borderRadius: 1,
            zIndex: 1,
            fontWeight: 'normal',
            color: 'inherit',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          {isTouchDevice ? 'Drag the map to move the marker' : 'Drag the marker to move it'}
        </Typography>
        <Typography
          variant='body2'
          sx={{
            position: 'absolute',
            top: 50,
            left: 10,
            bgcolor: 'rgba(255, 255, 255, 0.8)',
            p: 1,
            borderRadius: 1,
            zIndex: 1,
            fontWeight: 'normal',
            color: 'inherit',
          }}
        >
          Keyboard users: Use arrow keys to move the marker
        </Typography>
        <ReactMapGL
          {...viewState}
          onMove={handleMapMove}
          style={{ width: '100%', height: '100%' }}
          mapStyle={
            showSatellite
              ? 'mapbox://styles/mapbox/satellite-streets-v12'
              : 'mapbox://styles/mapbox/streets-v12'
          }
          mapboxAccessToken={MAPBOX_TOKEN}
          zoom={mapZoom}
          onZoom={(e) => {
            setMapZoom(e.viewState.zoom)
            announceToScreenReader(`Map zoom level ${e.viewState.zoom.toFixed(1)}`)
          }}
          onClick={handleOverlayPointClick}
          interactiveLayerIds={overlayGeometry ? ['overlay-point'] : []}
          cursor={overlayGeometry ? 'pointer' : undefined}
          aria-label='Map for selecting location'
        >
          <NavigationControl />

          {validAreaGeometry && (
            <Source
              id='validation-polygon'
              type='geojson'
              data={{
                type: 'Feature',
                geometry: JSON.parse(validAreaGeometry),
              }}
            >
              <Layer
                id='polygon-fill'
                type='fill'
                paint={{
                  'fill-color': '#e0f3fb',
                  'fill-opacity': 0.5,
                  'fill-outline-color': '#0066cc',
                }}
              />
              <Layer
                id='polygon-outline'
                type='line'
                paint={{
                  'line-color': '#2E7D32',
                  'line-width': 2,
                }}
              />
            </Source>
          )}

          {overlayGeometry && (
            <OverlayLayers
              overlayGeometry={overlayGeometry}
              overlayPopup={overlayPopup}
              onClosePopup={() => setOverlayPopup(null)}
            />
          )}

          {!isTouchDevice ? (
            <Marker
              longitude={selectedLocation.coordinates.lon}
              latitude={selectedLocation.coordinates.lat}
              color={isLocationValid ? 'red' : 'orange'}
              anchor='bottom'
              draggable
              onDragEnd={(event) => {
                handleMarkerDragEnd(event)
                const { lng, lat } = event.lngLat
                announceToScreenReader(
                  `Marker moved to latitude ${lat.toFixed(4)}, longitude ${lng.toFixed(4)}`,
                )
              }}
            >
              <Icon name='LocationOn' color='red' aria-hidden='true' />
              <div style={{ ...visuallyHidden }}>Location marker</div>
            </Marker>
          ) : (
            <Marker
              longitude={viewState.longitude}
              latitude={viewState.latitude}
              color='red'
              anchor='bottom'
            >
              <Icon name='LocationOn' color='red' aria-hidden='true' />
              <div style={{ ...visuallyHidden }}>Location marker</div>
            </Marker>
          )}
        </ReactMapGL>
        <Box sx={{ position: 'absolute', bottom: 10, right: 10, display: 'flex', gap: 1 }}>
          <IconButton
            onClick={() => {
              setShowSatellite(!showSatellite)
              announceToScreenReader(`Switched to ${showSatellite ? 'street' : 'satellite'} view`)
            }}
            aria-label={`Switch to ${showSatellite ? 'street' : 'satellite'} view`}
            aria-pressed={showSatellite}
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.8)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.9)',
              },
            }}
          >
            <Icon name='SatelliteAlt' />
          </IconButton>
        </Box>
      </Box>
      {!isLocationValid && renderInvalidAreaMessage()}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mt: 2,
        }}
      >
        <Button
          variant='text'
          width='fit'
          onClick={resetToSearchView}
          startIcon={<Icon name='KeyboardBackspace' />}
          ref={backButtonRef}
          aria-label='Go back to search view'
        >
          Back
        </Button>
        <Button
          variant='contained'
          ref={confirmButtonRef}
          disabled={!isLocationValid}
          onClick={async () => {
            announceToScreenReader('Getting address details for selected location...')
            try {
              const response = await fetch(
                `https://api.mapbox.com/geocoding/v5/mapbox.places/${viewState.longitude},${viewState.latitude}.json?access_token=${MAPBOX_TOKEN}`,
              )
              const data = await response.json()
              if (data.features?.[0]) {
                const addressComponents = parseMapboxAddress(data.features[0])
                setSelectedLocation((prev) => ({
                  ...prev,
                  address: addressComponents,
                }))
                announceToScreenReader('Address details retrieved. Moving to confirmation view.')
              }
            } catch (error) {
              console.error('Reverse geocoding error:', error)
              setError('Failed to get address details. You can enter them manually.')
              announceToScreenReader('Failed to get address details. You can enter them manually.')
            }
            setView('confirm')
          }}
          aria-label='Confirm selected location and proceed'
        >
          Confirm Location
        </Button>
      </Box>

      {/* Live region for map interactions */}
      <div aria-live='assertive' role='status' style={{ ...visuallyHidden }}>
        {announcement}
      </div>
    </Box>
  )

  const renderConfirmView = (): React.ReactNode => (
    <Box role='region' aria-label='Confirm address'>
      <Box
        sx={{ position: 'relative', height: 200, mb: 2, borderRadius: 1, overflow: 'hidden' }}
        tabIndex={0}
        aria-label='Map showing selected location'
        role='application'
      >
        <Box
          sx={{
            position: 'absolute',
            top: 10,
            left: 10,
            zIndex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
          }}
        >
          {view === 'manual' && (
            <Banner
              type='warning'
              variant='standard'
              body={`Verify the pin matches your address. ${isTouchDevice ? 'Drag the map to move the marker' : 'Drag the marker to move it'}`}
            />
          )}
        </Box>
        <ReactMapGL
          {...viewState}
          onMove={handleMapMove}
          style={{ width: '100%', height: '100%' }}
          mapStyle={
            showSatellite
              ? 'mapbox://styles/mapbox/satellite-streets-v12'
              : 'mapbox://styles/mapbox/streets-v12'
          }
          mapboxAccessToken={MAPBOX_TOKEN}
          zoom={mapZoom}
          onZoom={(e) => {
            setMapZoom(e.viewState.zoom)
            announceToScreenReader(`Map zoom level ${e.viewState.zoom.toFixed(1)}`)
          }}
          onClick={handleOverlayPointClick}
          interactiveLayerIds={overlayGeometry ? ['overlay-point'] : []}
          cursor={overlayGeometry ? 'pointer' : undefined}
          aria-label='Map showing selected address location'
        >
          <NavigationControl />

          {validAreaGeometry && (
            <Source
              id='validation-polygon'
              type='geojson'
              data={{
                type: 'Feature',
                geometry: JSON.parse(validAreaGeometry),
                properties: {},
              }}
            >
              <Layer
                id='polygon-fill'
                type='fill'
                paint={{
                  'fill-color': '#e0f3fb',
                  'fill-opacity': 0.5,
                  'fill-outline-color': '#0066cc',
                }}
              />
              <Layer
                id='polygon-outline'
                type='line'
                paint={{
                  'line-color': '#2E7D32',
                  'line-width': 2,
                }}
              />
            </Source>
          )}

          {overlayGeometry && (
            <OverlayLayers
              overlayGeometry={overlayGeometry}
              overlayPopup={overlayPopup}
              onClosePopup={() => setOverlayPopup(null)}
            />
          )}

          {!isTouchDevice ? (
            <Marker
              longitude={selectedLocation.coordinates.lon}
              latitude={selectedLocation.coordinates.lat}
              color='red'
              anchor='bottom'
              draggable
              onDragEnd={(event) => {
                handleMarkerDragEnd(event)
                const { lng, lat } = event.lngLat
                announceToScreenReader(
                  `Marker moved to latitude ${lat.toFixed(4)}, longitude ${lng.toFixed(4)}`,
                )
              }}
            >
              <Icon name='LocationOn' color='red' aria-hidden='true' />
              <div style={{ ...visuallyHidden }}>Location marker</div>
            </Marker>
          ) : (
            <Marker
              longitude={viewState.longitude}
              latitude={viewState.latitude}
              color='red'
              anchor='bottom'
            >
              <Icon name='LocationOn' color='red' aria-hidden='true' />
              <div style={{ ...visuallyHidden }}>Location marker</div>
            </Marker>
          )}
        </ReactMapGL>
        <Box sx={{ position: 'absolute', bottom: 10, right: 10, display: 'flex', gap: 1 }}>
          <IconButton
            onClick={() => {
              setShowSatellite(!showSatellite)
              announceToScreenReader(`Switched to ${showSatellite ? 'street' : 'satellite'} view`)
            }}
            aria-label={`Switch to ${showSatellite ? 'street' : 'satellite'} view`}
            aria-pressed={showSatellite}
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.8)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.9)',
              },
            }}
          >
            <Icon name='SatelliteAlt' />
          </IconButton>
        </Box>
      </Box>

      {!isLocationValid && renderInvalidAreaMessage()}

      {requestCoordinates && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <TextField
            label='Latitude'
            value={coordinateInputs.lat}
            onChange={(e) => {
              const value = e.target.value
              if (value === '' || value === '-' || value.match(/^-?\d*\.?\d*$/)) {
                setCoordinateInputs((prev) => ({ ...prev, lat: value }))
                const lat = parseFloat(value)
                if (!isNaN(lat) && lat >= -90 && lat <= 90) {
                  setSelectedLocation((prev) => ({
                    ...prev,
                    coordinates: { ...prev.coordinates, lat },
                  }))
                  setViewState((prev) => ({
                    ...prev,
                    latitude: lat,
                  }))
                  announceToScreenReader(`Latitude set to ${lat}`)
                }
              }
            }}
            inputProps={{
              'aria-label': 'Latitude',
              inputMode: 'decimal',
            }}
          />
          <TextField
            label='Longitude'
            value={coordinateInputs.lon}
            onChange={(e) => {
              const value = e.target.value
              if (value === '' || value === '-' || value.match(/^-?\d*\.?\d*$/)) {
                setCoordinateInputs((prev) => ({ ...prev, lon: value }))
                const lon = parseFloat(value)
                if (!isNaN(lon) && lon >= -180 && lon <= 180) {
                  setSelectedLocation((prev) => ({
                    ...prev,
                    coordinates: { ...prev.coordinates, lon },
                  }))
                  setViewState((prev) => ({
                    ...prev,
                    longitude: lon,
                  }))
                  announceToScreenReader(`Longitude set to ${lon}`)
                }
              }
            }}
            inputProps={{
              'aria-label': 'Longitude',
              inputMode: 'decimal',
            }}
          />
        </Box>
      )}

      <Box
        sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
        role='form'
        aria-label='Address form'
      >
        <TextField
          label='Street'
          value={selectedLocation.address.street}
          onChange={(e) =>
            setSelectedLocation((prev) => ({
              ...prev,
              address: { ...prev.address, street: e.target.value },
            }))
          }
          inputRef={streetInputRef}
          inputProps={{
            'aria-label': 'Street address',
          }}
        />
        <TextField
          label='Apartment, Unit, Ste'
          value={selectedLocation.address.unit}
          onChange={(e) =>
            setSelectedLocation((prev) => ({
              ...prev,
              address: { ...prev.address, unit: e.target.value },
            }))
          }
          inputProps={{
            'aria-label': 'Apartment or unit number (optional)',
          }}
        />
        <TextField
          label='City'
          value={selectedLocation.address.city}
          onChange={(e) =>
            setSelectedLocation((prev) => ({
              ...prev,
              address: { ...prev.address, city: e.target.value },
            }))
          }
          inputProps={{
            'aria-label': 'City',
          }}
        />
        <TextField
          label='State'
          value={selectedLocation.address.state}
          onChange={(e) => {
            const value = e.target.value
            setSelectedLocation((prev) => ({
              ...prev,
              address: { ...prev.address, state: value },
            }))
            if (value && !isValidState(value)) {
              setValidationErrors((prev) => ({ ...prev, state: 'Invalid state' }))
            } else {
              setValidationErrors((prev) => ({ ...prev, state: null }))
            }
          }}
          error={!!validationErrors.state}
          errorText={validationErrors.state || undefined}
          inputProps={{
            'aria-label': 'State',
          }}
        />
        <TextField
          label='ZIP'
          value={selectedLocation.address.zip}
          onChange={(e) => {
            const value = e.target.value
            setSelectedLocation((prev) => ({
              ...prev,
              address: { ...prev.address, zip: value },
            }))
            if (value && !isValidZip(value)) {
              setValidationErrors((prev) => ({ ...prev, zip: 'Invalid zip code' }))
            } else {
              setValidationErrors((prev) => ({ ...prev, zip: null }))
            }
          }}
          error={!!validationErrors.zip}
          errorText={validationErrors.zip || undefined}
          inputProps={{
            'aria-label': 'ZIP code',
            inputMode: 'numeric',
            pattern: '[0-9]*',
          }}
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mt: 3,
        }}
      >
        <Button
          variant='text'
          width='fit'
          onClick={resetToSearchView}
          startIcon={<Icon name='KeyboardBackspace' />}
          ref={backButtonRef}
          aria-label='Go back to search view'
        >
          Back
        </Button>
        <Button
          variant='contained'
          onClick={() => {
            handleSave()
            announceToScreenReader('Saving address...')
          }}
          disabled={
            !selectedLocation.address.street ||
            !selectedLocation.address.city ||
            !selectedLocation.address.state ||
            !selectedLocation.address.zip ||
            !isLocationValid ||
            !!validationErrors.state ||
            !!validationErrors.zip
          }
          aria-label='Save address'
        >
          Save
        </Button>
      </Box>

      {/* Live region for form interactions */}
      <div aria-live='polite' role='status' style={{ ...visuallyHidden }}>
        {announcement}
      </div>
    </Box>
  )

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth='sm'
      fullWidth
      aria-labelledby='location-entry-title'
      aria-describedby='location-entry-description'
      aria-modal='true'
      role='dialog'
    >
      <Box sx={{ p: 3 }}>
        <Typography id='location-entry-title' variant='h6' sx={{ mb: 1 }}>
          {title}
        </Typography>
        <Typography id='location-entry-description' variant='body2' sx={{ mb: 2 }}>
          {view === 'search' && 'Search for your address or select a location on the map.'}
          {view === 'manual' && 'Enter your address details manually.'}
          {view === 'map' &&
            'Select your location on the map. You can drag the marker or use arrow keys.'}
          {view === 'confirm' && 'Review and confirm your address details.'}
        </Typography>

        {view === 'search' && renderSearchView()}
        {view === 'manual' && renderManualView()}
        {view === 'map' && renderMapView()}
        {view === 'confirm' && renderConfirmView()}

        {error && (
          <Typography color='error' variant='body2' sx={{ mt: 1 }} role='alert'>
            {error}
          </Typography>
        )}

        {/* Global screen reader announcements */}
        <Snackbar
          open={showAnnouncement}
          autoHideDuration={3000}
          onClose={() => setShowAnnouncement(false)}
          sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', ...visuallyHidden }}
        >
          <Alert severity='info' sx={{ width: '100%' }}>
            {announcement}
          </Alert>
        </Snackbar>
      </Box>
    </Dialog>
  )
}

export default LocationEntryModal
